import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import ResultsIcon from '@pelckmans/elementis/icons/results';
import ShareIcon from '@pelckmans/elementis/icons/share';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getMedialinkById } from '../../../../../api/medialink';
import { getUserMaterial } from '../../../../../api/user-material';
import ModalContext from '../../../../../contexts/ModalContext';
import useNavigateResult from '../../../../../shared-components/elementis/hooks/useNavigateResult';
import TABS from '../../../../../shared-components/elementis/types/ResultsPageTabs';
import ShareModal from '../../../../../shared-components/share-modal/ShareModal';
import { formatAssignmentToDate } from '../../../../../utils/formatDate';
import { getFullHierarchyDescriptionOfNode } from '../../../../../utils/getNodeDescription';
import ModuleContext from '../../../../material/_contexts/ModuleContext';
import { MaterialIcon } from '../../../../material/components/MaterialIcon';
import { TeacherAssignment } from '../api/AssignmentTeacherApi';
import TeacherAssignmentsContext from '../contexts/TeacherAssignmentsContext';
import { EndDateCategory } from './AssignmentDivision';

const BASE = 'PAGES.ASSIGNMENTS.TEACHER.SECTION.TABLE.BODY.ASSIGNMENTS';

type AssignmentProps = {
  assignment: TeacherAssignment;
  endDateCategory: EndDateCategory;
};

export default function Assignment({ assignment, endDateCategory }: AssignmentProps) {
  const { t } = useTranslation();
  const { module, subModules } = useContext(ModuleContext);
  const { createResultsUrl } = useNavigateResult(module);
  const { currentSchoolYear } = useSchoolYear();
  const { fetchAssignments, archived, selectedSchoolYear } = useContext(TeacherAssignmentsContext);
  const { showModal } = useContext(ModalContext);

  const assignmentModule = [module, ...subModules].find(m => m!.id === assignment.moduleId);

  const locationIndicator = getFullHierarchyDescriptionOfNode(assignment.hierarchy, t);

  const { groupId, schoolId, userIds } = assignment.audience;

  async function handleShareModal() {
    try {
      const userMaterialResponse =
        assignment.materialType === 'user-material'
          ? await getUserMaterial(assignment.moduleId, {
              ids: assignment.materialId,
              includeNodeHierarchy: true,
              limit: 1,
            })
          : undefined;

      const medialink =
        assignment.materialType === 'medialink'
          ? await getMedialinkById(assignment.moduleId, assignment.materialId)
          : undefined;

      showModal(
        <ShareModal
          id="share-modal"
          moduleId={assignment.moduleId}
          nodeId={assignment.hierarchy.at(-1)?.id ?? ''}
          userMaterials={userMaterialResponse ? userMaterialResponse.data : undefined}
          medialinks={medialink ? [medialink] : undefined}
          onSubmitted={fetchAssignments}
        />,
      );
    } catch (error) {
      console.error('Error fetching data for ShareModal:', error);
    }
  }

  return (
    <tr className="table-item">
      <CellWithHierarchy
        scope="row"
        hierarchyProps={{ level: 1, hasCollapsibleParent: true }}
        minWidth="xl"
        maxWidth="xl"
      >
        <Cell.Text
          title={assignment.name}
          startAdornment={<MaterialIcon materialType={assignment.materialType} contentType={assignment.contentType} />}
        />
      </CellWithHierarchy>
      <Cell minWidth="md" maxWidth="md">
        {assignmentModule && (
          <Cell.Text
            title={locationIndicator}
            subTitle={{
              text: assignmentModule.name,
              position: 'bottom',
              startAdornment: (
                <Avatar>
                  <AvatarImage name={assignmentModule.niceName} src={assignmentModule.styling?.logo || ''} />
                </Avatar>
              ),
            }}
          />
        )}
      </Cell>
      <Cell
        pillGroupProps={{
          'aria-label': t(`${BASE}.PERIOD.A11Y_LABEL`),
          role: 'group',
        }}
        minWidth="sm"
        maxWidth="sm"
      >
        {new Date(assignment.from) > new Date() && (
          <Cell.Pill prominence="neutral">
            {t(`${BASE}.PERIOD.FROM`, { from: formatAssignmentToDate(assignment.from) })}
          </Cell.Pill>
        )}
        <Cell.Pill
          prominence={endDateCategory === 'TODAY' ? 'danger' : endDateCategory === 'THIS_WEEK' ? 'warning' : 'neutral'}
        >
          {t(`${BASE}.PERIOD.TO`, { to: formatAssignmentToDate(assignment.to) })}
        </Cell.Pill>
      </Cell>
      <Cell minWidth="md" maxWidth="md">
        <Cell.Text
          title={assignment.group || t(`${BASE}.ALL_GROUPS`)}
          subTitle={{
            text: assignment.school.address
              ? `${assignment.school.name}, ${assignment.school.address.city}`
              : assignment.school.name,
            position: 'bottom',
          }}
        />
      </Cell>
      <Cell
        minWidth="xs"
        pillGroupProps={{
          'aria-label': t(`${BASE}.STUDENTS.A11Y_LABEL`),
          role: 'group',
        }}
      >
        <Cell.Pill>
          {assignment.audience.userIds
            ? String(assignment.audience.userIds.length)
            : t(`${BASE}.STUDENTS.ALL_STUDENTS`)}
        </Cell.Pill>
      </Cell>
      <Cell
        buttonGroupProps={{
          'aria-label': t(`${BASE}.ACTIONS.A11Y_LABEL`),
          role: 'group',
          className: 'button-group',
        }}
        sticky
        className="actions-cell"
      >
        <Cell.Button
          startIcon={<ShareIcon data-testid="share-icon" />}
          prominence="neutral"
          aria-label={t(`${BASE}.ACTIONS.SHARE_BUTTON.A11Y_LABEL`)}
          tooltip={
            archived ? t(`${BASE}.ACTIONS.SHARE_BUTTON.TOOLTIP_ARCHIVED`) : t(`${BASE}.ACTIONS.SHARE_BUTTON.TOOLTIP`)
          }
          onClick={handleShareModal}
        />
        <Cell.Button
          as="link"
          startIcon={<ResultsIcon data-testid="results-icon" />}
          prominence="neutral"
          aria-label={t(`${BASE}.ACTIONS.RESULTS_BUTTON.A11Y_LABEL`)}
          tooltip={t(`${BASE}.ACTIONS.RESULTS_BUTTON.TOOLTIP`, { name: assignment.name })}
          href={createResultsUrl({
            school: schoolId,
            module: assignment.moduleId,
            exercises: [{ id: assignment.materialId }],
            groups: groupId ? [groupId] : undefined,
            memberGroups: userIds && groupId ? userIds.map(userId => ({ member: userId, group: groupId })) : undefined,
            selectedTab: TABS.EXERCISE,
            // The archive can show other years, results filters on the year the assignment ran in
            schoolYear:
              selectedSchoolYear?.id ?? `${currentSchoolYear.from.getFullYear()}-${currentSchoolYear.to.getFullYear()}`,
          })}
        />
      </Cell>
    </tr>
  );
}
